/** Reducing a table of rows to one value, by naming a column and how to fold it. */

/** The dropdown choice that means "no column". */
export const DASHBOARD_NO_COLUMN = '(none)';

/** How rows fold into one value. The dropdown offers these in this order, sorted by name. */
export const DASHBOARD_AGGREGATES = [
    'avg',
    'count',
    'countDistinct',
    'first',
    'max',
    'min',
    'sum',
] as const;
export type DashboardAggregate = (typeof DASHBOARD_AGGREGATES)[number];

/** What a config with no aggregate stored gets. */
export const DASHBOARD_DEFAULT_AGGREGATE = 'first' satisfies DashboardAggregate;

/** One cell read off a data row. Empty cells are dropped before any aggregate sees them. */
export type AggregateCell = string | number | boolean;

/** A data row keyed by column. */
export type AggregateRow = Readonly<Record<string, AggregateCell | null | undefined>>;

/** The finished number or text. */
export type AggregateValue = number | string | undefined;

/** Which column to reduce and how. */
export type ColumnAggregation = {
    // No column picked: missing in a config older than the field, or `DASHBOARD_NO_COLUMN`.
    readonly column?: string;
    readonly aggregate: DashboardAggregate;
    /** A boolean column that keeps a row when its cell is `true`.*/
    readonly where?: string;
};

/** What every aggregate is handed, built from the rows that passed the condition. */
type AggregateInput = {
    /** The value column's cells, with the empty ones already dropped. */
    readonly cells: readonly AggregateCell[];
    /** Only the finite numbers among `cells`. */
    readonly numbers: readonly number[];
    /** How many rows were left after the condition. */
    readonly rowCount: number;
};

/** Adds numbers up. Both `sum` and `avg` need this. */
function addUp(numbers: readonly number[]): number {
    return numbers.reduce((total, number) => total + number, 0);
}

const AGGREGATE_FUNCTIONS: Readonly<
    Record<DashboardAggregate, (input: AggregateInput) => AggregateValue>
> = {
    avg: ({ numbers }) => (numbers.length === 0 ? undefined : addUp(numbers) / numbers.length),

    count: ({ cells }) => cells.length,

    countDistinct: ({ cells }) => new Set(cells).size,

    first: ({ cells }) => {
        if (cells.length === 0) {
            return undefined;
        }
        // A true or false cell has no text worth printing, so show it as 1 or 0.
        const [firstCell] = cells;
        return typeof firstCell === 'boolean' ? Number(firstCell) : firstCell;
    },

    max: ({ numbers }) =>
        numbers.length === 0
            ? undefined
            : numbers.reduce((highest, number) => Math.max(highest, number)),

    min: ({ numbers }) =>
        numbers.length === 0
            ? undefined
            : numbers.reduce((lowest, number) => Math.min(lowest, number)),

    sum: ({ numbers, rowCount }) => {
        // Rows with no number here are hidden data not zero.
        if (numbers.length === 0) {
            return rowCount === 0 ? 0 : undefined;
        }
        return addUp(numbers);
    },
};

/** Nothing stored and `DASHBOARD_NO_COLUMN` both mean the author picked no column. */
export function pickedColumnName(column: string | undefined): string | undefined {
    return column === undefined || column === DASHBOARD_NO_COLUMN ? undefined : column;
}

/** Whether the author has named a value column, or picked the one aggregate that needs none. */
export function isAggregationConfigured(config: ColumnAggregation): boolean {
    return pickedColumnName(config.column) !== undefined || config.aggregate === 'count';
}

/** Reduces the rows to one value. Rows arrive already narrowed by the dashboard filters. */
export function calculateAggregate(
    rows: readonly AggregateRow[],
    config: ColumnAggregation
): AggregateValue {
    if (!isAggregationConfigured(config)) {
        return undefined;
    }

    const valueColumn = pickedColumnName(config.column);
    const conditionColumn = pickedColumnName(config.where);

    // A column no row carries was renamed or never sent. Aggregating it would print a wrong number.
    if (rows.length > 0 && valueColumn !== undefined && !rows.some((row) => valueColumn in row)) {
        return undefined;
    }
    if (
        rows.length > 0 &&
        conditionColumn !== undefined &&
        !rows.some((row) => conditionColumn in row)
    ) {
        return undefined;
    }

    const matchingRows =
        conditionColumn === undefined ? rows : rows.filter((row) => row[conditionColumn] === true);
    if (valueColumn === undefined) {
        return matchingRows.length;
    }

    const cells = matchingRows
        .map((row) => row[valueColumn])
        .filter((cell): cell is AggregateCell => cell !== null && cell !== undefined);
    const numbers = cells.filter(
        (cell): cell is number => typeof cell === 'number' && Number.isFinite(cell)
    );

    return AGGREGATE_FUNCTIONS[config.aggregate]({
        cells,
        numbers,
        rowCount: matchingRows.length,
    });
}
